import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { ItineraryCard } from '../components/ItineraryCard';
import { PriceCompare } from '../components/PriceCompare';
import { Seo } from '../components/Seo';
import { articleSchema, breadcrumb } from '../lib/structuredData';
import { ogImage } from '../lib/structuredData';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { api } from '../lib/api';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { formatPrice } from '../lib/currency';
import { toast } from 'sonner';
import { MapPin, Clock, Wallet, CalendarDays, BadgeCheck, Heart, Share2, Plane, Hotel, Ticket, ExternalLink, Info, ChevronRight, Home as HomeIcon, Map, Wifi, Car, ShieldCheck } from 'lucide-react';

const KIND_ICON = { flight: Plane, hotel: Hotel, experience: Ticket, tour: Map, esim: Wifi, transfer: Car, car_rental: Car, insurance: ShieldCheck, attraction: Ticket };

export default function ItineraryDetail() {
  const { username, slug } = useParams();
  const navigate = useNavigate();
  const { currency } = useApp();
  const { requireAuth } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.publicItinerary(username, slug).then(({ data }) => {
      setData(data);
    }).catch(() => navigate('/itineraries'));
  }, [username, slug, navigate]);

  if (!data) return <Layout><div className="max-w-4xl mx-auto px-4 py-20">Loading…</div></Layout>;
  const it = data.itinerary; const creator = data.creator;
  const canonical = data.seo?.canonical || `/creators/${username}/itineraries/${slug}`;
  const itinDesc = (data.seo?.description || `${it.duration_days}-day ${it.destination} itinerary by ${it.creator_name}.`).slice(0, 300);
  const itinJsonLd = [
    articleSchema({
      headline: it.title,
      description: itinDesc,
      image: it.cover_image,
      path: canonical,
      authorName: creator?.name || it.creator_name,
      authorPath: `/creators/${creator?.username || username}`,
      datePublished: it.published_at,
    }),
    breadcrumb([
      { name: 'Home', path: '/' },
      { name: 'Itineraries', path: '/itineraries' },
      { name: it.title },
    ]),
  ];

  const openOffer = (kind, offer) => {
    // Route through the correct vertical tracker; provider deep links are resolved server-side.
    const KNOWN = ['flight', 'hotel', 'experience', 'tour', 'esim', 'insurance', 'visa', 'store'];
    const type = KNOWN.includes(kind) ? kind : 'experience';
    const url = api.itineraryGoUrl(type, offer.offer_id, {
      creator_id: it.creator_id, itinerary_id: it.itinerary_id, day_id: offer.day_id || '', placement: 'itinerary',
    });
    window.open(url, '_blank');
  };
  const save = () => requireAuth(async () => {
    try { const { data } = await api.saveItinerary(it.itinerary_id); toast.success(data.saved ? 'Saved!' : 'Removed'); }
    catch { toast.error('Could not save'); }
  });
  const share = () => { navigator.clipboard?.writeText(window.location.href); toast.success('Link copied'); };

  return (
    <Layout>
      <Seo title={it.title} description={itinDesc} path={canonical}
        image={ogImage(`itinerary/${creator?.username || username}/${slug}.png`)} type="article" jsonLd={itinJsonLd} />
      <section className="relative h-[380px]">
        <img src={it.cover_image || 'https://images.unsplash.com/photo-1540959733332-eab4deabeeaf?w=1400&q=80'} alt={it.title} className="w-full h-full object-cover" />
        <div className="absolute inset-0 hero-overlay" />
        <div className="absolute inset-0 flex items-end">
          <div className="max-w-5xl w-full mx-auto px-4 sm:px-6 lg:px-8 pb-8 text-primary-foreground">
            <nav className="flex items-center gap-1 text-xs text-primary-foreground/70 mb-2"><Link to="/" className="hover:text-accent flex items-center gap-1"><HomeIcon className="h-3 w-3" /> Home</Link><ChevronRight className="h-3 w-3" /><Link to="/itineraries" className="hover:text-accent">Itineraries</Link><ChevronRight className="h-3 w-3" /><span className="text-primary-foreground">{it.destination}</span></nav>
            {it.content_type === 'sponsored' && <Badge className="bg-amber-500 text-white rounded-full mb-2">Paid Partnership</Badge>}
            <h1 className="font-display text-3xl sm:text-4xl font-semibold max-w-3xl">{it.title}</h1>
            <div className="mt-3 flex items-center gap-3">
              <Link to={`/creators/${creator.username}`} className="flex items-center gap-2">
                <span className="grid place-items-center h-9 w-9 rounded-full bg-accent text-accent-foreground font-semibold">{(creator.name||'C').charAt(0)}</span>
                <span className="text-sm">by <span className="font-semibold">{creator.name}</span> {creator.verification_status === 'verified' && <BadgeCheck className="h-4 w-4 inline text-accent" />}</span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex items-center justify-between gap-3 mb-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 flex-1">
            {[[MapPin, 'Destination', it.destination], [Clock, 'Duration', `${it.duration_days} days`], [Wallet, 'Budget', it.estimated_budget ? formatPrice(it.estimated_budget, currency) : '—'], [CalendarDays, 'Best time', it.best_time || '—']].map(([Ic, l, v], idx) => (
              <div key={idx} className="rounded-xl border border-border bg-card p-3"><div className="flex items-center gap-1.5 text-xs text-muted-foreground"><Ic className="h-3.5 w-3.5 text-accent" /> {l}</div><p className="font-semibold text-sm mt-0.5 truncate">{v}</p></div>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-2 mb-5">
          <Button onClick={save} variant="outline" className="rounded-xl" data-testid="save-itinerary-button"><Heart className="h-4 w-4 mr-1.5" /> Save</Button>
          <Button onClick={share} variant="outline" className="rounded-xl" data-testid="share-itinerary-button"><Share2 className="h-4 w-4 mr-1.5" /> Share</Button>
          <Button onClick={() => navigate(`/flights/results?origin=CCU&destination=${it.destination_code}&adults=1&cabin=economy`)} className="rounded-xl bg-accent text-accent-foreground hover:bg-accent/90 ml-auto" data-testid="itinerary-compare-flights"><Plane className="h-4 w-4 mr-1.5" /> Compare flights</Button>
        </div>

        <div className="rounded-xl border border-border bg-secondary/50 p-3 text-xs text-muted-foreground flex items-start gap-2 mb-6"><Info className="h-4 w-4 mt-0.5 shrink-0 text-accent" /> Some links in this itinerary may be affiliate links. FlightDeals.in and/or the creator may earn a commission when you book through them. This does not increase the price you pay.</div>

        {/* Day-by-day timeline */}
        <div className="space-y-5" data-testid="itinerary-days">
          {(it.days || []).map((day, di) => (
            <div key={day.day_id || di} className="rounded-2xl border border-border bg-card p-5">
              <div className="flex items-center gap-3 mb-2">
                <span className="grid place-items-center h-10 w-10 rounded-xl bg-accent/10 text-accent font-semibold">{day.day || di + 1}</span>
                <div><p className="font-display font-semibold text-lg">{day.title || `Day ${day.day || di + 1}`}</p></div>
              </div>
              {day.description && <p className="text-sm text-muted-foreground">{day.description}</p>}
              {['morning','afternoon','evening'].map((slot) => day[slot] ? (
                <p key={slot} className="text-sm mt-1"><span className="font-medium capitalize">{slot}:</span> <span className="text-muted-foreground">{day[slot]}</span></p>
              ) : null)}
              {Array.isArray(day.activities) && day.activities.length > 0 && (
                <ul className="mt-2 text-sm text-muted-foreground list-disc pl-5">{day.activities.map((a, i) => <li key={i}>{a}</li>)}</ul>
              )}
              {day.tips && <p className="mt-2 text-sm bg-secondary/60 rounded-lg p-2"><span className="font-medium">Tip:</span> {day.tips}</p>}
              {Array.isArray(day.offers) && day.offers.length > 0 && (
                <div className="mt-3 grid gap-2 sm:grid-cols-2">
                  {day.offers.map((offer, oi) => { const Ic = KIND_ICON[offer.kind] || Ticket;
                    const isRich = (offer.kind === 'esim' || offer.kind === 'insurance') && offer.details && Object.keys(offer.details || {}).length > 0;
                    const detailEntries = Object.entries(offer.details || {}).filter(([, v]) => v);
                    return (
                    <div key={oi} className="rounded-xl border border-border p-3 flex flex-col gap-2" data-testid="itinerary-offer">
                      <div className="flex items-center gap-3">
                        <span className="grid place-items-center h-9 w-9 rounded-lg bg-accent/10 text-accent shrink-0"><Ic className="h-4 w-4" /></span>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5">
                            <p className="text-sm font-medium truncate">{offer.title || offer.name}</p>
                            {offer.price_status && <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-full border ${offer.price_status === 'LIVE' ? 'text-emerald-600 border-emerald-500/40' : offer.price_status === 'INDICATIVE' ? 'text-amber-600 border-amber-500/40' : 'text-accent border-accent/40'}`}>{offer.price_status}</span>}
                          </div>
                          {offer.price ? <p className="text-xs text-muted-foreground">{formatPrice(offer.price_inr || offer.price, currency)}{offer.provider ? ` · via ${String(offer.provider).replace(/[_:]/g, ' ')}` : ''}</p> : (offer.subtitle ? <p className="text-xs text-muted-foreground truncate">{offer.subtitle}</p> : null)}
                        </div>
                        <Button size="sm" onClick={() => openOffer(offer.kind, { ...offer, day_id: day.day_id })} className="rounded-lg bg-accent text-accent-foreground hover:bg-accent/90 shrink-0" data-testid="itinerary-view-deal">View <ExternalLink className="h-3 w-3 ml-1" /></Button>
                      </div>
                      {isRich && (
                        <div className="flex flex-wrap gap-1.5 pl-12" data-testid={`offer-details-${oi}`}>
                          {detailEntries.map(([k, v]) => (
                            <span key={k} className="text-[11px] rounded-md bg-secondary px-2 py-0.5 text-muted-foreground"><span className="font-medium text-foreground/80">{k}:</span> {v}</span>
                          ))}
                        </div>
                      )}
                      {offer.fd_id && (
                        <div className="pl-12"><PriceCompare fdId={offer.fd_id} /></div>
                      )}
                    </div>
                  ); })}
                </div>
              )}
            </div>
          ))}
          {(!it.days || it.days.length === 0) && <p className="text-muted-foreground text-sm">This itinerary has no day details yet.</p>}
        </div>

        {/* Complete your trip */}
        <div className="mt-8 rounded-2xl bg-primary text-primary-foreground p-6">
          <h3 className="font-display font-semibold text-xl mb-3">Complete your {it.destination} trip</h3>
          <div className="grid gap-3 sm:grid-cols-3">
            <Button onClick={() => navigate(`/flights/results?origin=CCU&destination=${it.destination_code}&adults=1&cabin=economy`)} className="bg-accent text-accent-foreground hover:bg-accent/90 rounded-xl"><Plane className="h-4 w-4 mr-1.5" /> Flights</Button>
            <Button onClick={() => navigate(`/hotels?destination_code=${it.destination_code}`)} variant="outline" className="rounded-xl bg-white/10 border-white/20 text-primary-foreground hover:bg-white/20"><Hotel className="h-4 w-4 mr-1.5" /> Hotels</Button>
            <Button onClick={() => navigate(`/experiences?destination_code=${it.destination_code}`)} variant="outline" className="rounded-xl bg-white/10 border-white/20 text-primary-foreground hover:bg-white/20"><Ticket className="h-4 w-4 mr-1.5" /> Experiences</Button>
          </div>
        </div>

        {data.related?.length > 0 && (
          <div className="mt-8"><h3 className="font-display font-semibold text-xl mb-4">More {it.destination} itineraries</h3><div className="grid gap-4 sm:grid-cols-3">{data.related.map((r) => <ItineraryCard key={r.itinerary_id} it={r} currency={currency} />)}</div></div>
        )}
      </section>
    </Layout>
  );
}
